
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useReviewStore } from '@/lib/stores/reviewStore';
import { Review, PerkRating } from '@/lib/types';
import { categorizeJobTitle } from '@/utils/jobCategorization';

export const useCompanyReviews = (companyName: string) => {
  const { reviews: localReviews } = useReviewStore();
  const [supabaseReviews, setSupabaseReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const decodedCompanyName = decodeURIComponent(companyName || '');
  
  useEffect(() => {
    const fetchSupabaseReviews = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .eq('company', decodedCompanyName)
          .is('is_hidden', false); // Only fetch visible reviews
          
        if (error) {
          console.error("Error fetching reviews from Supabase:", error);
          return;
        }
        
        // Fetch perk ratings for these reviews
        const reviewIds = data.map(review => review.id);
        const { data: perkRatingData, error: perkRatingError } = await supabase
          .from('perk_ratings')
          .select('*')
          .in('review_id', reviewIds);
          
        if (perkRatingError) {
          console.error("Error fetching perk ratings:", perkRatingError);
        }
        
        // Map perk ratings to reviews
        const perkRatingsMap: Record<string, PerkRating[]> = {};
        if (perkRatingData) {
          perkRatingData.forEach(rating => {
            if (!perkRatingsMap[rating.review_id]) {
              perkRatingsMap[rating.review_id] = [];
            }
            
            perkRatingsMap[rating.review_id].push({
              perkId: rating.perk_id,
              perkText: rating.perk_text,
              isPositive: rating.is_positive,
              comment: rating.comment || undefined
            });
          });
        }
        
        // Convert Supabase reviews to the application's Review format
        const formattedReviews: Review[] = data.map(review => ({
          id: review.id,
          company: review.company,
          jobTitle: review.job_title,
          reasonForExit: review.reason_for_exit,
          location: review.location,
          exitDate: review.exit_date ? new Date(review.exit_date) : undefined,
          fairWorkInvolved: review.fair_work_involved,
          positiveExperience: review.positive_experience,
          createdAt: new Date(review.created_at).getTime(),
          jobCategory: categorizeJobTitle(review.job_title),
          isHidden: review.is_hidden,
          salary: review.salary,
          unique_ref: review.unique_ref, // Add the unique_ref field
          perkRatings: perkRatingsMap[review.id] || []
        }));
        
        setSupabaseReviews(formattedReviews);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSupabaseReviews();
  }, [decodedCompanyName]);
  
  // Process local reviews to add job categories and unique_ref
  const localCompanyReviews = localReviews
    .filter(review => review.company.toLowerCase() === decodedCompanyName.toLowerCase())
    .map(review => ({
      ...review,
      jobCategory: review.jobCategory || categorizeJobTitle(review.jobTitle),
      unique_ref: review.unique_ref
    }));
  
  // Combine reviews from both sources
  const allCompanyReviews = [...localCompanyReviews, ...supabaseReviews];
  
  return {
    isLoading,
    allCompanyReviews,
    decodedCompanyName
  };
};
